import { Icon } from '@iconify/react'
import { Card } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/Skeleton'
import { DateDisplay } from '@/components/shared/DateDisplay'
import { useTenderTimeline, useTenderVersions } from '@/hooks/useTenders'
import { cn } from '@/lib/utils'
import { PROJECT_STATUS_LABELS } from '@/lib/constants'
import type { ProjectStatus } from '@/lib/pocketbase'

interface TimelineTabProps {
  tenderId: string
}

export function TenderTimelineTab({ tenderId }: TimelineTabProps) {
  const { data: events, isLoading: eventsLoading } = useTenderTimeline(tenderId)
  const { data: versions, isLoading: versionsLoading } = useTenderVersions(tenderId)

  const isLoading = eventsLoading || versionsLoading

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4">
              <Skeleton variant="circular" className="w-10 h-10" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    )
  }

  // Combine and sort timeline entries
  const timelineItems = [
    ...(events || []).map((e) => ({
      type: 'status' as const,
      date: e.occurred_at,
      data: e,
    })),
    ...(versions || []).map((v) => ({
      type: 'version' as const,
      date: v.published_at || v.effective_at,
      data: {
        ...v,
        version_number: (v as any).version_number ?? (v as any).version_seq,
        project_status: (v as any).project_status,
      },
    })),
  ]
    .filter(
      (
        item
      ): item is {
        type: 'status' | 'version'
        date: string
        data: any
      } => Boolean(item.date)
    )
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  if (timelineItems.length === 0) {
    return (
      <Card className="p-8 text-center">
        <Icon
          icon="solar:history-line-duotone"
          className="w-12 h-12 text-slate-300 mx-auto mb-3"
        />
        <h3 className="font-semibold text-slate-900">ยังไม่มีเหตุการณ์</h3>
        <p className="text-sm text-slate-500 mt-1">
          การเปลี่ยนแปลงสถานะจะแสดงที่นี่
        </p>
      </Card>
    )
  }

  return (
    <Card padding="none">
      <div className="p-6 border-b border-slate-100">
        <h3 className="font-display font-semibold text-slate-900">
          ไทม์ไลน์โครงการ
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          บันทึก {timelineItems.length} เหตุการณ์
        </p>
      </div>

      <div className="p-6">
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200" />

          {/* Timeline items */}
          <div className="space-y-6">
            {timelineItems.map((item, index) => (
              <TimelineItem
                key={`${item.type}-${index}`}
                item={item}
                isFirst={index === 0}
              />
            ))}
          </div>
        </div>
      </div>
    </Card>
  )
}

function TimelineItem({
  item,
  isFirst,
}: {
  item: { type: 'status' | 'version'; date: string; data: any }
  isFirst: boolean
}) {
  const isStatus = item.type === 'status'
  const statusValue = isStatus ? item.data.status : item.data.project_status
  const statusLabel = PROJECT_STATUS_LABELS[statusValue as ProjectStatus]?.pro || statusValue || 'ไม่ทราบสถานะ'

  const getStatusIcon = (status: string) => {
    const icons: Record<string, string> = {
      'tor-prep': 'solar:document-text-line-duotone',
      'purchase-request': 'solar:clipboard-list-line-duotone',
      'invitation-issued': 'solar:letter-opened-line-duotone',
      'award-approved': 'solar:cup-star-line-duotone',
      'contract-management': 'solar:document-medicine-line-duotone',
      'project-cancelled': 'solar:close-circle-line-duotone',
    }
    return icons[status] || 'solar:info-circle-line-duotone'
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'tor-prep': 'bg-slate-100 text-slate-600',
      'purchase-request': 'bg-amber-100 text-amber-600',
      'invitation-issued': 'bg-blue-100 text-blue-600',
      'award-approved': 'bg-emerald-100 text-emerald-600',
      'contract-management': 'bg-violet-100 text-violet-600',
      'project-cancelled': 'bg-rose-100 text-rose-600',
    }
    return colors[status] || 'bg-slate-100 text-slate-600'
  }

  return (
    <div className="relative flex gap-4">
      {/* Icon */}
      <div
        className={cn(
          'relative z-10 w-10 h-10 rounded-full flex items-center justify-center',
          isStatus ? getStatusColor(statusValue) : 'bg-slate-100 text-slate-600'
        )}
      >
        <Icon
          icon={
            isStatus
              ? getStatusIcon(statusValue)
              : 'solar:refresh-circle-line-duotone'
          }
          className="w-5 h-5"
        />
      </div>

      {/* Content */}
      <div className="flex-1 pt-1">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="font-medium text-slate-900">
              {isStatus
                ? `สถานะ ${statusLabel}`
                : `สร้างเวอร์ชัน ${item.data.version_number}`}
            </h4>
            {isFirst && (
              <span className="inline-block px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full mt-1">
                ล่าสุด
              </span>
            )}
          </div>
          <span className="text-sm text-slate-500 flex-shrink-0">
            <DateDisplay date={item.date} format="relative" />
          </span>
        </div>

        <div className="mt-2 text-sm text-slate-500">
          <DateDisplay date={item.date} format="datetime" />
          {isStatus && statusValue && (
            <span className="ml-2">({statusLabel})</span>
          )}
        </div>

        {item.data.note && (
          <p className="mt-2 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
            {item.data.note}
          </p>
        )}
      </div>
    </div>
  )
}
